/*
  Conso — bloco de prazo de entrega (PDP).

  Fluxo:
  1. Resolve o CEP sem digitação: localStorage `__hdf_last_cep` (gravado pelo
     widget do app hub-de-frete e por este bloco) → rota geo do app (IP→CEP
     aproximado, server-side) → campo de CEP inline como último recurso.
  2. Cota na rota pública do app: GET {apiUrl}?zip&declaredValueCents&items
     (mesmo contrato do widget da PDP — items: [{grams, quantity,
     requires_shipping, variant_id, product_id}]).
  3. Renderiza "Frete Grátis|R$ X - Chegará entre [inicio] e [fim]" usando
     min–max dos deliveryDate dos serviços retornados, countdown até o
     horário limite e o texto de devolução.

  Contrato esperado da rota geo (a implementar no app):
    GET {geoUrl} → 200 {"zip": "01310930"} | qualquer outra coisa = miss.

  Reage a troca de variante via evento `variant:update` do Horizon
  (peso/preço vêm do mapa de variantes embutido pelo Liquid do bloco).
*/
(function () {
  'use strict';

  if (customElements.get('conso-prazo-entrega')) return;

  var HDF_LAST_CEP_KEY = '__hdf_last_cep';
  var GEO_CACHE_KEY = '__conso_prazo_geo';
  var QUOTE_CACHE_PREFIX = '__conso_prazo_q_';
  var GEO_TIMEOUT_MS = 4000;

  function onlyDigits(v) {
    return String(v || '').replace(/\D/g, '');
  }

  function isValidCep(zip) {
    return /^\d{8}$/.test(zip);
  }

  function readStorage(storage, key) {
    try {
      return storage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStorage(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (e) {
      /* modo privado — aceitável */
    }
  }

  function formatBRL(value) {
    try {
      return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    } catch (e) {
      return 'R$ ' + value.toFixed(2).replace('.', ',');
    }
  }

  function formatCep(zip) {
    return zip.slice(0, 5) + '-' + zip.slice(5);
  }

  var dateFmt;
  function formatDate(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      dateFmt = dateFmt || new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short' });
      return dateFmt.format(d);
    } catch (e) {
      return d.getDate() + '/' + (d.getMonth() + 1);
    }
  }

  // Formato do prazo próprio: "segunda (20/jul)" — dia da semana + (dia/mês abrev.).
  var WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
  var MONTHS_ABBR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  function formatWeekdayDate(date) {
    var d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    return WEEKDAYS[d.getDay()] + ' (' + d.getDate() + '/' + MONTHS_ABBR[d.getMonth()] + ')';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var ConsoPrazoEntrega = /** @type {any} */ (function () {
    function El() {
      return Reflect.construct(HTMLElement, [], El);
    }
    El.prototype = Object.create(HTMLElement.prototype);

    El.prototype.connectedCallback = function () {
      var self = this;
      this.apiUrl = this.dataset.apiUrl;
      this.geoUrl = this.dataset.geoUrl;
      this.productId = parseInt(this.dataset.productId, 10) || 0;
      this.variantId = parseInt(this.dataset.variantId, 10) || 0;
      this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
      this.showPrice = this.dataset.showPrice !== 'false';
      this.daysBefore = parseInt(this.dataset.daysBefore, 10);
      if (isNaN(this.daysBefore) || this.daysBefore < 0) this.daysBefore = 2;
      // Dias mínimos de processamento antes da primeira data exibível.
      this.minDays = parseInt(this.dataset.minDays, 10);
      if (isNaN(this.minDays) || this.minDays < 0) this.minDays = 1;
      this.cepSource = null; // 'exact' | 'geo'
      this.currentCep = null;
      this.lastRequestKey = null;
      this.countdownTimer = null;

      var mapEl = this.querySelector('[data-conso-prazo-variants]');
      this.variants = {};
      if (mapEl) {
        try {
          this.variants = JSON.parse(mapEl.textContent) || {};
        } catch (e) {
          this.variants = {};
        }
      }

      this.els = {
        skeleton: this.querySelector('.conso-prazo__skeleton'),
        result: this.querySelector('.conso-prazo__result'),
        headline: this.querySelector('[data-conso-prazo-headline]'),
        countdown: this.querySelector('[data-conso-prazo-countdown]'),
        tooltip: this.querySelector('.conso-prazo__tooltip'),
        infoBtn: this.querySelector('.conso-prazo__info-btn'),
        services: this.querySelector('[data-conso-prazo-services]'),
        tooltipCep: this.querySelector('[data-conso-prazo-cep-label]'),
        form: this.querySelector('.conso-prazo__cep-form'),
        input: this.querySelector('.conso-prazo__cep-input'),
        error: this.querySelector('.conso-prazo__cep-error'),
      };

      if (this.els.infoBtn) {
        this.els.infoBtn.addEventListener('click', function () {
          var hidden = self.els.tooltip.hasAttribute('hidden');
          if (hidden) self.els.tooltip.removeAttribute('hidden');
          else self.els.tooltip.setAttribute('hidden', '');
          self.els.infoBtn.setAttribute('aria-expanded', hidden ? 'true' : 'false');
        });
      }

      if (this.els.form) {
        this.els.form.addEventListener('submit', function (ev) {
          ev.preventDefault();
          var zip = onlyDigits(self.els.input.value);
          if (!isValidCep(zip)) {
            self.showCepError('Informe um CEP válido.');
            return;
          }
          self.hideCepError();
          // CEP digitado é exato — compartilha com o widget do app.
          writeStorage(localStorage, HDF_LAST_CEP_KEY, zip);
          self.setCep(zip, 'exact');
          self.quote();
        });
        this.els.input.addEventListener('input', function () {
          var d = onlyDigits(self.els.input.value).slice(0, 8);
          self.els.input.value = d.length > 5 ? formatCep(d) : d;
        });
      }

      var changeBtn = this.querySelector('.conso-prazo__change-cep');
      if (changeBtn) {
        changeBtn.addEventListener('click', function () {
          self.showForm();
        });
      }

      this.onVariantUpdate = function (ev) {
        var resource = ev && ev.detail && ev.detail.resource;
        if (!resource || !resource.id) return;
        var id = parseInt(resource.id, 10);
        if (!id || id === self.variantId) return;
        self.variantId = id;
        if (self.currentCep) self.quote();
      };
      document.addEventListener('variant:update', this.onVariantUpdate);

      this.resolveCep().then(function (found) {
        if (found) self.quote();
        else self.showForm();
      });
    };

    El.prototype.disconnectedCallback = function () {
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      if (this.onVariantUpdate) document.removeEventListener('variant:update', this.onVariantUpdate);
    };

    // --- Resolução de CEP -------------------------------------------------

    El.prototype.setCep = function (zip, source) {
      this.currentCep = zip;
      this.cepSource = source;
    };

    El.prototype.resolveCep = function () {
      var self = this;
      var remembered = onlyDigits(readStorage(localStorage, HDF_LAST_CEP_KEY));
      if (isValidCep(remembered)) {
        this.setCep(remembered, 'exact');
        return Promise.resolve(true);
      }

      var cachedGeo = onlyDigits(readStorage(sessionStorage, GEO_CACHE_KEY));
      if (isValidCep(cachedGeo)) {
        this.setCep(cachedGeo, 'geo');
        return Promise.resolve(true);
      }
      // Miss de geo já registrado nesta sessão — não tenta de novo.
      if (readStorage(sessionStorage, GEO_CACHE_KEY) === 'miss') {
        return Promise.resolve(false);
      }

      if (!this.geoUrl) return Promise.resolve(false);

      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = controller && setTimeout(function () { controller.abort(); }, GEO_TIMEOUT_MS);

      return fetch(this.geoUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller ? controller.signal : undefined,
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          var zip = onlyDigits(data && data.zip);
          if (isValidCep(zip)) {
            writeStorage(sessionStorage, GEO_CACHE_KEY, zip);
            self.setCep(zip, 'geo');
            return true;
          }
          writeStorage(sessionStorage, GEO_CACHE_KEY, 'miss');
          return false;
        })
        .catch(function () {
          writeStorage(sessionStorage, GEO_CACHE_KEY, 'miss');
          return false;
        })
        .finally(function () {
          if (timer) clearTimeout(timer);
        });
    };

    // --- Cotação ----------------------------------------------------------

    El.prototype.currentVariant = function () {
      return this.variants[String(this.variantId)] || null;
    };

    El.prototype.quote = function () {
      var self = this;
      var v = this.currentVariant();
      if (!v || v.rs === false) {
        this.showForm();
        return;
      }

      var items = [{
        grams: v.g || 0,
        quantity: 1,
        requires_shipping: true,
        variant_id: this.variantId,
        product_id: this.productId,
      }];

      var requestKey = this.currentCep + '|' + this.variantId;
      this.lastRequestKey = requestKey;

      var cached = readStorage(sessionStorage, QUOTE_CACHE_PREFIX + requestKey);
      if (cached) {
        try {
          var parsed = JSON.parse(cached);
          if (parsed && parsed.length) {
            this.render(parsed);
            return;
          }
        } catch (e) { /* cache corrompido — segue pra rede */ }
      }

      this.showSkeleton();

      var url = new URL(this.apiUrl, window.location.origin);
      url.searchParams.set('zip', this.currentCep);
      url.searchParams.set('declaredValueCents', String(v.p || 0));
      url.searchParams.set('items', JSON.stringify(items));

      fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then(function (res) { return res.json().catch(function () { return null; }); })
        .then(function (data) {
          if (self.lastRequestKey !== requestKey) return; // resposta velha
          var results = Array.isArray(data) ? data : (data && Array.isArray(data.results) ? data.results : null);
          if (results && results.length) {
            writeStorage(sessionStorage, QUOTE_CACHE_PREFIX + requestKey, JSON.stringify(results));
            self.render(results);
          } else {
            self.handleQuoteFailure();
          }
        })
        .catch(function () {
          if (self.lastRequestKey !== requestKey) return;
          self.handleQuoteFailure();
        });
    };

    El.prototype.handleQuoteFailure = function () {
      // CEP aproximado (geo) pode não ter cobertura — cai pro campo manual.
      this.showForm();
      if (this.cepSource === 'exact') {
        this.showCepError('Não foi possível calcular. Confira o CEP.');
      }
    };

    // --- Render -----------------------------------------------------------

    El.prototype.render = function (results) {
      var showPrice = this.showPrice;
      var cheapest = null;
      var fastest = null;
      var fastestKey = Infinity;
      results.forEach(function (r) {
        var price = typeof r.price === 'number' ? r.price : (typeof r.price_cents === 'number' ? r.price_cents / 100 : null);
        if (price != null && (cheapest === null || price < cheapest)) cheapest = price;
        // "Mais rápida" = entrega mais cedo. Rankeia pela data de entrega da API;
        // serviço sem data (modo oculto no app) fica de fora do ranking.
        if (r.deliveryDate) {
          var t = new Date(r.deliveryDate).getTime();
          if (!isNaN(t) && t < fastestKey) {
            fastestKey = t;
            fastest = r;
          }
        }
      });

      // Frete grátis sempre mostra "Frete Grátis" (é o argumento de venda).
      // Frete pago: com valor ("Frete R$ 8,00") ou só o prefixo quando o
      // operador desliga a exibição do valor.
      var label;
      if (cheapest === 0 || cheapest === null) {
        label = this.dataset.freeLabel;
      } else if (showPrice) {
        label = (this.dataset.paidPrefix + ' ' + formatBRL(cheapest)).trim();
      } else {
        label = this.dataset.paidPrefix;
      }

      // Prazo próprio (não a descrição do app): janela terminando na data da
      // transportadora mais rápida e começando em (data - daysBefore dias).
      // Formato "segunda (20/jul) e quarta (22/jul)". Quando daysBefore=0 (ou a
      // subtração cai no mesmo dia) usa o single_template. Vazio quando o app
      // oculta a data (modo HIDDEN) — aí só o label aparece.
      //
      // Piso de processamento: a rota do app cota só o trânsito da
      // transportadora, sem o tempo de separação/postagem. Em CEPs perto do CD
      // (ex.: Curitiba, 2 dias) a subtração do daysBefore jogava o início da
      // faixa pra hoje ou pra ontem — promessa impossível. Nenhuma ponta da
      // faixa pode ser anterior a hoje + minDays.
      var deliveryText = '';
      if (fastest && fastest.deliveryDate) {
        var endDate = new Date(fastest.deliveryDate);
        if (!isNaN(endDate.getTime())) {
          var floorDate = new Date();
          floorDate.setHours(0, 0, 0, 0);
          floorDate.setDate(floorDate.getDate() + this.minDays);
          if (endDate.getTime() < floorDate.getTime()) endDate = new Date(floorDate);
          var startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - this.daysBefore);
          if (startDate.getTime() < floorDate.getTime()) startDate = new Date(floorDate);
          var endStr = formatWeekdayDate(endDate);
          var startStr = formatWeekdayDate(startDate);
          deliveryText = startStr === endStr
            ? this.dataset.singleTemplate.replace('[fim]', endStr)
            : this.dataset.rangeTemplate.replace('[inicio]', startStr).replace('[fim]', endStr);
        }
      }

      this.els.headline.innerHTML =
        '<span class="conso-prazo__label">' + escapeHtml(label) + '</span>' +
        (deliveryText ? ' - <span>' + escapeHtml(deliveryText) + '</span>' : '');

      if (this.els.services) {
        var html = '';
        results.forEach(function (r) {
          var name = r.serviceName || r.name || 'Serviço';
          var price = typeof r.price === 'number' ? r.price : (typeof r.price_cents === 'number' ? r.price_cents / 100 : null);
          var priceText = price === 0 ? 'Grátis' : (price != null ? formatBRL(price) : '-');
          var delivery = typeof r.deliveryText === 'string' && r.deliveryText
            ? ' · ' + r.deliveryText
            : (r.deliveryDate ? ' · até ' + formatDate(r.deliveryDate) : '');
          html += '<div class="conso-prazo__service"><span>' + escapeHtml(name) + escapeHtml(delivery) + '</span>' +
            (showPrice ? '<span class="conso-prazo__service-price">' + escapeHtml(priceText) + '</span>' : '') + '</div>';
        });
        this.els.services.innerHTML = html;
      }

      if (this.els.tooltipCep) {
        this.els.tooltipCep.textContent = 'Prazos para o CEP ' + formatCep(this.currentCep) +
          (this.cepSource === 'geo' ? ' (aproximado pela sua região)' : '');
      }

      this.startCountdown();
      this.showResult();
    };

    // --- Countdown até o cutoff -------------------------------------------

    El.prototype.startCountdown = function () {
      var self = this;
      if (!this.els.countdown || isNaN(this.cutoffHour)) return;
      if (this.countdownTimer) clearInterval(this.countdownTimer);

      function tick() {
        var now = new Date();
        var cutoff = new Date(now);
        cutoff.setHours(self.cutoffHour, 0, 0, 0);
        var ms = cutoff.getTime() - now.getTime();
        if (ms <= 0) {
          self.els.countdown.setAttribute('hidden', '');
          clearInterval(self.countdownTimer);
          return;
        }
        var totalMin = Math.floor(ms / 60000);
        var h = Math.floor(totalMin / 60);
        var m = totalMin % 60;
        var tempo = h > 0 ? h + 'h ' + m + 'min' : m + 'min';
        self.els.countdown.textContent = self.dataset.countdownTemplate.replace('[tempo]', tempo);
        self.els.countdown.removeAttribute('hidden');
      }

      tick();
      this.countdownTimer = setInterval(tick, 30000);
    };

    // --- Estados ----------------------------------------------------------

    El.prototype.showSkeleton = function () {
      this.els.skeleton.removeAttribute('hidden');
      this.els.result.setAttribute('hidden', '');
      if (this.els.form) this.els.form.setAttribute('hidden', '');
    };

    El.prototype.showResult = function () {
      this.els.skeleton.setAttribute('hidden', '');
      this.els.result.removeAttribute('hidden');
      if (this.els.form) this.els.form.setAttribute('hidden', '');
      if (this.els.tooltip) this.els.tooltip.setAttribute('hidden', '');
      if (this.els.infoBtn) this.els.infoBtn.setAttribute('aria-expanded', 'false');
    };

    El.prototype.showForm = function () {
      this.els.skeleton.setAttribute('hidden', '');
      this.els.result.setAttribute('hidden', '');
      if (!this.els.form) return;
      this.els.form.removeAttribute('hidden');
      if (this.currentCep) this.els.input.value = formatCep(this.currentCep);
    };

    El.prototype.showCepError = function (msg) {
      if (!this.els.error) return;
      this.els.error.textContent = msg;
      this.els.error.removeAttribute('hidden');
    };

    El.prototype.hideCepError = function () {
      if (this.els.error) this.els.error.setAttribute('hidden', '');
    };

    return El;
  })();

  customElements.define('conso-prazo-entrega', ConsoPrazoEntrega);
})();
